import React, { useState } from "react";
import { styled } from "@mui/system";
import emailjs from "@emailjs/browser";
import {Title, Subtitle, SectionContainer,
BoxContainer,
Card,
CardText,
BoxContainerTitle,
Price,
CTAText,
SuccessMessage
} from "../assets/styles/ContentStyles"

const FeaturesList = styled("ul")({
  listStyle: "none",
  padding: 0,
  margin: "0 0 30px 0",
  flex: 1,
  width: "100%",
});

const PriceSection = styled("div")({
  width: "100%",
  marginBottom: "25px",
  textAlign: "center",
});

const EmailInput = styled("input")({
  width: "100%",
  padding: "14px 20px",
  backgroundColor: "rgba(30, 41, 59, 0.8)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  borderRadius: "8px",
  color: "#fff",
  fontSize: "15px",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  outline: "none",
  transition: "all 0.3s ease",
  marginBottom: "15px",

  "&::placeholder": {
    color: "#94a3b8",
  },

  "&:focus": {
    border: "1px solid rgba(112, 240, 163, 0.5)",
    boxShadow: "0 0 12px rgba(112, 240, 163, 0.2)",
    backgroundColor: "rgba(30, 41, 59, 0.95)",
  },
});

const JoinButton = styled("button")({
  width: "100%",
  padding: "14px 32px",
  background: "#70F0A3",
  color: "#000",
  border: "none",
  borderRadius: "50px",
  fontSize: "15px",
  fontWeight: "700",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  cursor: "pointer",
  transition: "all 0.3s ease",

  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 20px rgba(112, 240, 163, 0.4)",
    background: "#5FE092",
  },

  "&:active": {
    transform: "translateY(0)",
  },
});

const Pricing = () => {
  const [emails, setEmails] = useState({});
  const [submittedPlans, setSubmittedPlans] = useState({});

  const pricingPlans = [
    {
      name: "Home & Family",
      features: [
        "Up to 20 devices",
        "Real-time vulnerability alerts",
        "Weekly security digest",
        "Complete device dashboard",
        "Step-by-step fix guidance",
      ],
      price: "$9",
      period: "/mo",
      isCustom: false,
    },
    {
      name: "Small to Medium Business $69/month",
      features: [
        "Up to 100 devices",
        "Everything in Home & Family",
        "Priority support",
        "Advanced reporting",
        "Multi-location management",
      ],
      price: "$29",
      period: "/mo",
      isCustom: false,
    },
    {
      name: "Enterprise - Custom Pricing",
      features: [
        "100 devices",
        "Everything in Home & Family",
        "Priority support",
        "Advanced reporting",
      ],
      customText: "Custom solutions for organizations with 100+ devices",
      isCustom: true,
    },
  ];

  const handleEmailChange = (planName, value) => {
    setEmails({ ...emails, [planName]: value });
  };

  const handleJoinWaitlist = async (e, planName) => {
    e.preventDefault();
    const email = emails[planName];

    if (email) {
      setSubmittedPlans({ ...submittedPlans, [planName]: true });
      console.log("Joining waitlist for:", planName, "with email:", email);

      try {
        await emailjs.send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_JOIN_WAITLIST_TEMPLATE_ID,
          {
            user_email: email,
            waitlist_type: `Pricing - ${planName}`
          },
          process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        ).then(
          function (response) {
            console.log('SUCCESS!', response);
          },
          function (err) {
            console.log('FAILED...', err);
          }
        );
      } catch (error) {
        console.error("Error sending email:", error);
      }

      // Reset after 5 seconds
      setTimeout(() => {
        setEmails({ ...emails, [planName]: "" });
        setSubmittedPlans({ ...submittedPlans, [planName]: false });
      }, 5000);
    }
  };

  return (
    <SectionContainer>
      <Title>Pricing</Title>
      <Subtitle>Find the device security plan that's right for you.</Subtitle>

      <BoxContainer>
        {pricingPlans.map((plan, index) => (
          <Card key={index}>
            <BoxContainerTitle>{plan.name}</BoxContainerTitle>

            <FeaturesList>
              {plan.features.map((feature, idx) => (
                <CardText key={idx} style={{textAlign: "left", marginBottom: "12px"}}>{feature}</CardText>
              ))}
            </FeaturesList>

            <PriceSection>
              {plan.isCustom ? (
                <CTAText style={{margin: "0", padding: "0"}}>{plan.customText}</CTAText>
              ) : (
                <Price>
                  {plan.price}
                  <span>{plan.period}</span>
                </Price>
              )}
            </PriceSection>

            {!submittedPlans[plan.name] ? (
              <form onSubmit={(e) => handleJoinWaitlist(e, plan.name)}>
                <EmailInput
                  type="email"
                  placeholder="Email address"
                  value={emails[plan.name] || ""}
                  onChange={(e) => handleEmailChange(plan.name, e.target.value)}
                  required
                />
                <JoinButton type="submit">
                  Join the Waitlist
                </JoinButton>
              </form>
            ) : (
              <SuccessMessage>
                Thanks for joining! We'll be in touch soon.
              </SuccessMessage>
            )}
          </Card>
        ))}
      </BoxContainer>
    </SectionContainer>
  );
};

export default Pricing;
